import * as React from 'react';
import { useEffect, useState } from 'react';
import * as _ from 'lodash';
import { observer } from 'mobx-react';
import { useFormik } from 'formik';

import { MANAGED_ROLE } from '@journeyapps-platform/types-cardinal';
import { V5BaseModel } from '@journeyapps-platform/lib-journey-core';

import { Keys, useKeyPress } from '../../../../hooks/useKeyPress';
import { MAX_INPUT_FIELD_LENGTH, validateEmail, validateLength } from '../../../../utils/Validators';
import { useOrgStore } from '../../../../hooks/useOrgStore';
import { useDialogStore } from '../../../../hooks/useDialogService';
import { EditDevFormWidget } from './EditDevFormWidget';
import { System } from '../../../../System';
import { DialogStore } from '../../../../stores/DialogStore';
import { OrgStore } from '../../../../stores/OrgStore';
import { useSystem } from '../../../../hooks/useSystem';
import { JourneyNavigationHandler } from '../../../../routes';

export interface EditDeveloperValues {
  email: string;
  roleIds: string[];
}

export interface DeveloperContext {
  org_id: string;
  model_id?: string;
}

export interface AbstractDeveloperModel extends V5BaseModel<any> {}

export interface EditDeveloperSubmitParams<T extends AbstractDeveloperModel> {
  context: {
    org_id: string;
    model_id?: string;
  };
  developerReference: T | null;
  stores: {
    system: System;
    dialogStore: DialogStore;
    orgStore: OrgStore;
  };
  values: EditDeveloperValues;
}

export interface AbstractEditDeveloperDialogWidgetProps<T extends AbstractDeveloperModel> {
  contextProvider: () => DeveloperContext;
  developerProvider: (id?: string) => Promise<T | null>;
  formSubmitter: (params: EditDeveloperSubmitParams<T>) => Promise<any>;
  initialValuesProvider: (developer: T, roleOptionsIds: string[]) => EditDeveloperValues;
  onSubmitOrCancel: () => any;
  handler: JourneyNavigationHandler<any>;
}

export const showErrorDialog = async (dialogStore: DialogStore, message?: string) => {
  return dialogStore.errorDialog({
    subTitle: 'Failed to add developer',
    errorMessage:
      message || `Oh no, something went wrong and this developer couldn't be added. Please try again in a few minutes.`
  });
};

export const AbstractEditDeveloperDialogWidget = observer(
  <T extends AbstractDeveloperModel>(props: AbstractEditDeveloperDialogWidgetProps<T>) => {
    const { contextProvider, developerProvider, formSubmitter, initialValuesProvider, onSubmitOrCancel } = props;

    const { model_id, org_id } = contextProvider();

    const system = useSystem();
    const orgStore = useOrgStore();
    const dialogStore = useDialogStore();

    const [loading, setLoading] = useState(false);
    const [developerReference, setDeveloperReference] = useState<T | null>(null);

    const roleOptions = orgStore.current_org?.roles.items ?? [];
    const defaultRole = roleOptions.find((roleModel) => roleModel.options.role.name == MANAGED_ROLE.DEVELOPER);

    // Make sure all the org options are loaded
    useEffect(() => {
      orgStore.current_org?.loadRoles();
    }, [orgStore.current_org]);

    // Update developer model from url params
    useEffect(() => {
      developerProvider(model_id).then(setDeveloperReference);
    }, [model_id]);

    const formik = useFormik<EditDeveloperValues>({
      initialValues: {
        email: '',
        roleIds: defaultRole ? [defaultRole.id] : []
      },
      validateOnBlur: false,
      validate: (values) => {
        const errors: Partial<EditDeveloperValues> = {};
        if (!values.email) {
          errors.email = 'This field is required';
        } else if (!validateEmail(values.email)) {
          errors.email = 'Please enter a valid email address';
        }
        if (!validateLength(values.email || '')) {
          errors.email = `Email address must be less than ${MAX_INPUT_FIELD_LENGTH} characters`;
        }
        return errors;
      },

      onSubmit: (values) => {
        return formSubmitter({
          context: contextProvider(),
          developerReference,
          values,
          stores: {
            system,
            orgStore,
            dialogStore
          }
        });
      }
    });

    // Update formik values from user
    useEffect(() => {
      if (!developerReference) {
        return;
      }

      const roleOptionIds = roleOptions.map((roleModel) => roleModel.id);

      formik.setValues(initialValuesProvider(developerReference, roleOptionIds));
    }, [developerReference, loading]);

    // Allow for ENTER to submit
    const submitDisabled = !formik.values.email;
    const pressed = useKeyPress(Keys.ENTER);
    useEffect(() => {
      if (pressed && !submitDisabled) {
        setLoading(true);
        formik.submitForm().finally(() => {
          setLoading(false);
        });
      }
    }, [pressed, submitDisabled]);

    return (
      <EditDevFormWidget
        formik={formik}
        loading={loading}
        onCancel={onSubmitOrCancel}
        onSubmit={onSubmitOrCancel}
        org_id={org_id}
        developer={developerReference}
      />
    );
  }
);
